import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useAppSelector, useAppDispatch} from 'store/customer';
import {shallowEqual} from 'react-redux';
import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import {
    getRoomType,
    getRoomDimensions,
    getProducts,
    getShowCabinets,
    getProductValuesForUpdate,
    setProductValuesForUpdate,
    getSelectedProductValue,
    setSelectedProductValue,
    getPreviewPosition,
    getIsWireframeMode,
    getDoorFrontsMode,
    setDoorFrontsAnimating,
    getShowArrows,
    getIsRotating,
    getBackupProductValues,
    setBackupProductValues,
    setRoomType,
    setRoomLayoutModalOpen,
    setIsTopView,
    setRectangularDimension,
    setLShapedDimension,
    setLayoutId,
    getLayoutId,
    getIsEditingRoomDimension,
    setLayoutData,
    setToInitialState,
    setIsCreatingSnapshots,
    getHiddenWall,
    setRoomPlannerRoomIndex,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import useRendererSetup from 'components/customer/Preview3DCommon/lib/useRendererSetup';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {
    applyDoorFrontsMode,
    type DoorPoseTrigger,
} from 'components/customer/RoomPlanner/helpers/doorFronts';
import useRoomLayout from 'components/customer/RoomPlanner/lib/useRoomLayout';
import useWallHider from 'components/customer/RoomPlanner/lib/useWallHider';
import useRoomProduct from 'components/customer/RoomPlanner/lib/useRoomProduct';
import {useParams, useSearchParams} from 'react-router-dom';
import {useDebouncedCallback} from 'use-debounce';
import {JobContext} from 'contexts/JobContext';
import {
    CabinetMesh,
    LayoutRoomPlaceholder,
    RoomPlannerProductWithTemplate,
} from 'components/customer/RoomPlanner/types';
import {
    PreviewPosition,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import {PartialJob} from 'shared/types/PartialJob';
import {
    Event,
    genericPubSub,
} from 'components/customer/RoomPlanner/helper/GenericPubSub';
import {submitProductEditorAndContinue} from 'components/customer/RoomPlanner/helper/submitProductEditor';

import {useRoomSnapshots} from 'components/customer/RoomPlannerPDF/lib/useRoomSnapshots';
import {getGroupWidth} from 'components/customer/RoomPlanner/helpers/calculations';
import {renderCabinetGroup} from 'components/customer/RoomPlanner/helpers/renderCabinetGroup';
import {preloadCabinetTextures} from 'components/customer/Preview3DCommon/shapes/advancedShelf';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import {
    isRoomUpdatedSet,
    selectIsRoomUpdated,
} from 'components/customer/Room/store/roomSlice';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import {
    useGetLayoutsQuery,
    roomPlannerApi,
} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {getProductDifferences} from 'components/customer/RoomPlanner/helpers/getProductDifferences';
import {selectSelectedProduct} from 'components/customer/QFPRedux/store/qfpSlice';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import useAutoSave from 'components/customer/RoomPlanner/lib/useAutoSave';
import {overlapValidator} from './useCabinetOverlaps';
import {
    clearKeyboardSnapGuide,
    createKeyboardHandler,
    stopHeldMovement,
} from 'components/customer/RoomPlanner/helpers/keyboardControls';
import {handleFillerSwitching} from 'components/customer/RoomPlanner/helpers/fillerSwitching';
import {
    applyCabinetFormUpdate,
    findCabinetsForProductUpdate,
    isCabinetUpdateReady,
    persistCabinetUpdateOnSave,
    resolveProductValueForUpdate,
    scheduleCabinetDeselect,
    scheduleCabinetUpdateUi,
    syncCabinetDoorAfterUpdate,
    tryBeginCabinetUpdateSession,
} from 'components/customer/RoomPlanner/helpers/cabinetUpdate';
import {restorePlaceholdersForRoom} from 'components/customer/RoomPlanner/helpers/placeholder/loadPlaceholderGLB';
import {clearPlaceholdersFromScene} from 'components/customer/RoomPlanner/helpers/placeholder/removePlaceholder';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';
import {useGetRoomPreviewQuery} from 'components/customer/Preview3D/store/previewApi';
import {useUnsavedChangesDialog} from 'shared/components/ConfirmationDialog/UnsavedChangesDialog';

export type ComputedDimension = {
    computedWidth: number;
    roomWidth: number;
    roomDepth: number;
    roomDepthComputed: number;
    wallHeight: number;
    scale: number;
    roomLeftDepth: number;
    roomRightDepth: number;
    roomLeftWidth: number;
    roomRightWidth: number;
    positionX?: number;
    defaultPositionZ?: number;
    roomType?: string;
};

// Let the browser paint the room between cabinets so the startup overlay
// can show progress without waiting for every mesh to finish.
const EMPTY_ROOM_PLACEHOLDERS: LayoutRoomPlaceholder[] = [];

const waitForNextPaint = () =>
    new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
    });

const useRoomPlanner = (preloadingData: boolean) => {
    const dispatch = useAppDispatch();

    const [dragPosition, setDragPosition] = useState<THREE.Vector3>(null);
    const [reloader, setReloader] = useState(0);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [productsReady, setProductsReady] = useState(false);
    // Counts finished room-scene builds. Products are not drawn while this is 0.
    const [roomSceneGeneration, setRoomSceneGeneration] = useState(0);
    const renderedProductCountRef = useRef(0);
    const productRenderProgressSubscribersRef = useRef(new Set<() => void>());

    const roomType = useAppSelector(getRoomType);
    const showCabinets = useAppSelector(getShowCabinets);
    const isRoomUpdated = useAppSelector(selectIsRoomUpdated);
    const rotating = useAppSelector(getIsRotating);
    const showArrows = useAppSelector(getShowArrows);
    const doorFrontsMode = useAppSelector(getDoorFrontsMode);
    const roomPlannerData = useAppSelector(getProducts, shallowEqual);
    const {rectangularDimension, lShapedDimension} = useAppSelector(
        getRoomDimensions,
        shallowEqual
    );
    const previewPosition = useAppSelector(getPreviewPosition, shallowEqual);
    const {
        selectedCabinetGroup,
        setSelectedCabinetGroup,
        cabinetGroupListRef,
        placeholderListRef,
        originalDepthRef,
        originalHeightRef,
        originalWidthRef,
        setDeleteHandler,
        setCloseProductEditorMenu: setContextCloseProductEditorMenu,
    } = useRoomPlannerContext();
    const {debouncedSave} = useAutoSave();
    const isWireframeMode = useAppSelector(getIsWireframeMode);
    const backupProductValues = useAppSelector(
        getBackupProductValues,
        shallowEqual
    );
    const selectedProductValue = useAppSelector(
        getSelectedProductValue,
        shallowEqual
    );
    const productValuesForUpdate = useAppSelector(
        getProductValuesForUpdate,
        shallowEqual
    );
    const qfpSelectedProduct = useAppSelector(
        selectSelectedProduct,
        shallowEqual
    );
    const layoutId = useAppSelector(getLayoutId);
    const isEditingRoomDimension = useAppSelector(getIsEditingRoomDimension);
    const hiddenWall = useAppSelector(getHiddenWall);

    const mountRef = useRef<HTMLDivElement>(null);
    const previewPositionRef = useRef<PreviewPosition>(null);
    const updateInitialized = useRef(false);
    const isInitialRender = useRef(true);
    // Bumped when a new product render starts so an older loop can stop.
    const productRenderGeneration = useRef(0);
    const animationFrameId = useRef<number | null>();
    const isEditingRoomDimensionRef = useRef<boolean>(isEditingRoomDimension);
    const layoutIdRef = useRef(layoutId);
    const isSwitchingRooms = useRef(false);
    // Bumped when the room changes so a slow placeholder load cannot paint
    // the previous room's appliances into the next room.
    const placeholderRoomLoad = useRef(0);
    const originalExteriorThicknessRef = useRef<number | null>(null);
    /** Side-attached filler / vermin positions captured at edit-session start for cancel restore. */
    const attachedSideOriginalPositionsRef =
        useRef<Map<string, THREE.Vector3>>(null);
    const isRoomPreview = useRoomPreview();

    const publishRenderedProductCount = useCallback(
        (renderedProductCount: number) => {
            renderedProductCountRef.current = renderedProductCount;
            productRenderProgressSubscribersRef.current.forEach((notify) =>
                notify()
            );
        },
        []
    );

    const subscribeToProductRenderProgress = useCallback(
        (onProgress: () => void) => {
            productRenderProgressSubscribersRef.current.add(onProgress);
            return () => {
                productRenderProgressSubscribersRef.current.delete(onProgress);
            };
        },
        []
    );

    const getRenderedProductCount = useCallback(
        () => renderedProductCountRef.current,
        []
    );

    const jobContext = useContext(JobContext) as PartialJob | undefined;

    const roomFromJobContext = jobContext?.room;
    const {roomId: paramRoomIdFromUrl, roomSlug = ''} = useParams();

    const {data: roomPreviewData} = useGetRoomPreviewQuery(
        {slug: roomSlug},
        {
            skip: !isRoomPreview,
        }
    );

    const room = isRoomPreview ? roomPreviewData?.room : roomFromJobContext;

    const roomId = isRoomPreview
        ? roomPreviewData?.room.id
        : paramRoomIdFromUrl;
    const {renderer, setupRenderer} = useRendererSetup();
    const [searchParams, setSearchParams] = useSearchParams();
    const {isLoading, hasProductsToLoad} = useRoomProduct();
    const {
        data: layoutsFromApi,
        currentData: layoutsForCurrentRoom,
        isUninitialized: isLayoutUninitialized,
    } = useGetLayoutsQuery(parseInt(String(roomId)), {
        skip:
            isRoomPreview ||
            !roomId ||
            String(roomId) === 'undefined' ||
            Number.isNaN(parseInt(String(roomId))),
    });
    const layouts = isRoomPreview
        ? roomPreviewData?.layout
            ? {
                  success: roomPreviewData.success ?? 1,
                  layout: roomPreviewData.layout,
              }
            : undefined
        : layoutsFromApi;
    // A room that is still being configured (shape / height) has no saved
    // layout yet, so there is no startup work to wait for.
    const hasSavedRoomLayout = layouts?.success === 1 && !!layouts?.layout;
    const {
        createLShapedWalls,
        createLShapedFloor,
        createRectangularFloor,
        createRectangularWalls,
        wallRefs,
        sideButtonGroupsRef,
    } = useRoomLayout(
        showArrows,
        dragPosition,
        selectedCabinetGroup,
        getGroupWidth,
        cabinetGroupListRef,
        showCabinets,
        rotating
    );
    useWallHider(wallRefs, roomType);
    const {createSnapshots} = useRoomSnapshots();
    const {dialog, showDialog} = useUnsavedChangesDialog();

    const cabinetId = searchParams.get('cabinetId');
    const productId = searchParams.get('product');
    const isEditing = !!cabinetId;

    const {cabinet: checkIfOverlapsOtherCabinets} = overlapValidator;

    /*
     * Both buttons below restore cabinet editor state, which a placeholder
     * has none of: `backupProductValues` only ever holds a cabinet, and the
     * re-click loop walks the cabinet list. Running either for a placeholder
     * pulls the editor onto an unrelated cabinet, so `isPlaceholder` keeps the
     * prompt to its discard/keep decision.
     */
    const closeProductEditorMenu = (
        yesHandler?: () => void,
        isPlaceholder = false
    ) => {
        showDialog({
            onCancel: () => {
                if (isPlaceholder) return;

                cabinetGroupListRef.current.forEach((cabinet) => {
                    productEvents.triggerEvents(
                        cabinet,
                        'onClick',
                        cabinet.userData.values?.id !== parseInt(cabinetId)
                    );
                });
            },
            onConfirmDiscard: () => {
                if (isPlaceholder) {
                    yesHandler?.();
                    return;
                }

                void genericPubSub.publish(
                    Event.ROOM_PLANNER_CLEAR_PRODUCT_EDITOR,
                    undefined
                );
                yesHandler?.();
                checkForBackupProductValues(
                    true,
                    backupProductValues as unknown as PreviewFormValues
                );
            },
            onConfirmSave: async () => {
                await submitProductEditorAndContinue(yesHandler);
            },
        });
    };

    useEffect(() => {
        productEvents
            .setSearchParams(searchParams)
            .setUpdateSearchParams(setSearchParams)
            .setCloseProductEditorMenu(closeProductEditorMenu)
            .setIsPreview(isRoomPreview);

        // Connect room planner context to existing product editing cancellation flow
        setContextCloseProductEditorMenu?.(closeProductEditorMenu);
    }, [
        searchParams,
        closeProductEditorMenu,
        setContextCloseProductEditorMenu,
        isRoomPreview,
    ]);

    useEffect(() => {
        if (placeholderListRef) {
            productEvents.setPlaceholderListRef(placeholderListRef);
        }
        if (cabinetGroupListRef) {
            productEvents.setCabinetGroupListRef(cabinetGroupListRef);
        }
    }, [placeholderListRef, cabinetGroupListRef]);

    useEffect(() => {
        const onScreenShot = () => {
            if (
                renderer.current &&
                CameraSetup.scene &&
                CameraSetup.camera &&
                CameraSetup.controls
            ) {
                setIsGeneratingReport(true);
                // Call the async function but don't return the promise
                void createSnapshots(
                    CameraSetup.scene,
                    CameraSetup.camera,
                    renderer.current
                );
            }
        };
        if (!!layouts?.layout) {
            genericPubSub.subscribe(Event.ROOM_PLANNER_SNAPSHOT, onScreenShot);
        }
        return () => {
            genericPubSub.unsubscribe(
                Event.ROOM_PLANNER_SNAPSHOT,
                onScreenShot
            );
        };
    }, [layouts]);
    useEffect(() => {
        const onScreenShotComplete = () => {
            setIsGeneratingReport(false);
            dispatch(setIsCreatingSnapshots(false));
        };

        const onDelete = (cabinetId: number) => {
            if (cabinetId > 0) {
                const cabinetToDelete = cabinetGroupListRef.current.filter(
                    (group) => group.userData.values.job_cabinet_id == cabinetId
                );

                cabinetToDelete.forEach((cabinet) => {
                    cabinet.userData.deleteCabinet();
                });

                setSelectedCabinetGroup(null);
            }
        };

        genericPubSub.subscribe(Event.ROOM_PLANNER_CART_DELETE, onDelete);
        genericPubSub.subscribe(
            Event.ROOM_PLANNER_SNAPSHOT_COMPLETED,
            onScreenShotComplete
        );

        return () => {
            genericPubSub.unsubscribe(Event.ROOM_PLANNER_CART_DELETE, onDelete);
            genericPubSub.unsubscribe(
                Event.ROOM_PLANNER_SNAPSHOT_COMPLETED,
                onScreenShotComplete
            );
        };
    }, []);

    useEffect(() => {
        if (!roomType) return;
        if (preloadingData) return;
        if (!mountRef.current) return;

        const renderer = setupRenderer(mountRef);
        if (!renderer.current) return;
        renderer.current.shadowMap.enabled = true;
        renderer.current.shadowMap.type = THREE.PCFSoftShadowMap;
        // The room is mostly static: CameraSetup re-renders the shadow map
        // only when a shadow caster changes (see updateShadowsOnChange).
        renderer.current.shadowMap.autoUpdate = false;
        renderer.current.shadowMap.needsUpdate = true;

        mountRef.current.id = '3d-room-planner';

        productEvents
            .setRenderer(renderer.current)
            .setContainer(mountRef.current)
            .setSetDragPosition(setDragPosition);

        const cameraLocal = CameraSetup.setupCamera(mountRef, renderer.current);
        previewPositionRef.current =
            (layoutIdRef.current || layoutId) &&
            !isEditingRoomDimensionRef.current
                ? 'RIGHT'
                : previewPositionRef.current;

        CameraSetup.setupCameraPosition(
            previewPositionRef.current,
            roomType === 'LSHAPED'
                ? lShapedDimension.leftWidth
                : rectangularDimension.length
        );

        const textureLoader = new THREE.TextureLoader();
        const isLeftLeveledStaticView =
            previewPositionRef.current === 'LEFT_LEVELED_STATIC';

        if (roomType === 'LSHAPED') {
            createLShapedWalls(
                CameraSetup.scene,
                lShapedDimension,
                cameraLocal,
                renderer,
                isLeftLeveledStaticView
            );
            createLShapedFloor(
                CameraSetup.scene,
                textureLoader,
                lShapedDimension,
                previewPositionRef.current === 'TOP',
                isLeftLeveledStaticView
            );
        }

        if (roomType === 'RECTANGULAR') {
            createRectangularFloor(
                CameraSetup.scene,
                textureLoader,
                rectangularDimension,
                previewPositionRef.current === 'TOP',
                isLeftLeveledStaticView
            );
            createRectangularWalls(
                CameraSetup.scene,
                rectangularDimension,
                cameraLocal,
                renderer,
                isLeftLeveledStaticView
            );
        }

        // Re-add placeholders after scene setup (scene.clear in setupCamera removes them)
        placeholderListRef?.current?.forEach((p) => {
            if (p.parent !== CameraSetup.scene) {
                CameraSetup.scene.add(p);
            }
        });
        productEvents.setProducts(cabinetGroupListRef.current);
        if (CameraSetup.scene && CameraSetup.camera && CameraSetup.controls) {
            productEvents.attachEvents({
                scene: CameraSetup.scene,
                camera: CameraSetup.camera,
                controls: CameraSetup.controls,
                roomType,
            });
        }

        const animate = () => {
            animationFrameId.current = requestAnimationFrame(animate);
            CameraSetup.controls?.update();

            renderer.current.clear();
            renderer.current.clearDepth();
            renderer.current.render(CameraSetup.scene, cameraLocal);

            CameraSetup.checkCameraZone();

            TWEEN.update();
        };
        animate();

        cameraLocal.updateProjectionMatrix();

        // Scene setup clears any cabinets drawn earlier. Mark the room scene
        // ready only after that, so products are rendered into the finished room.
        isInitialRender.current = true;
        publishRenderedProductCount(0);
        setRoomSceneGeneration((generation) => generation + 1);

        // Follow the canvas container rather than the window: rotating the
        // device, toggling the sidebar or the half width canvas in mobile
        // landscape all change its size.
        const handleResize = () => {
            if (!mountRef.current || !renderer.current || !cameraLocal) return;

            const {width, height} = mountRef.current.getBoundingClientRect();
            if (width <= 0 || height <= 0) return;

            renderer.current.setSize(width, height);
            CameraSetup.resizeViewport(width, height);
        };

        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(mountRef.current);

        return () => {
            renderer.current?.dispose();
            mountRef.current?.removeChild(renderer.current.domElement);
            resizeObserver.disconnect();

            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
                animationFrameId.current = null;
            }
        };
    }, [
        roomType,
        rectangularDimension,
        lShapedDimension,
        reloader,
        preloadingData,
    ]);

    const clearEvents = () => {
        if (roomType) {
            productEvents.clearEvents();
            disposeCabinets();
        }
    };

    useEffect(() => {
        return () => {
            clearEvents();
        };
    }, [roomType]);

    useEffect(() => {
        // Products wait until the room scene exists. setupCamera() clears the
        // scene, so drawing before this wipes the cabinets on a slow refresh.
        if (roomSceneGeneration === 0) return;

        if (
            (roomType &&
                layouts?.success === 1 &&
                !isLoading &&
                isInitialRender.current &&
                !isEditingRoomDimensionRef.current) ||
            // if editing room dimension is true, it should always render products
            isEditingRoomDimensionRef.current
        ) {
            void renderProducts();
            dispatch(isRoomUpdatedSet(false));
        }
    }, [
        isLoading,
        roomPlannerData,
        roomType,
        rectangularDimension,
        lShapedDimension,
        reloader,
        layouts,
        roomSceneGeneration,
    ]);

    useEffect(() => {
        if (isRoomUpdated) {
            productEvents.clearEvents();
            isInitialRender.current = true;
        }
    }, [isRoomUpdated]);

    useEffect(() => {
        setReloader(1);
        if (CameraSetup && previewPositionRef.current == null)
            previewPositionRef.current = 'RIGHT';

        return () => {
            isInitialRender.current = true;
            productEvents.clearEvents();
            // Invalidate RoomPlannerLayout tag when component unmounts to prevent inconsistent positioning of cabinets
            dispatch(roomPlannerApi.util.invalidateTags(['RoomPlannerLayout']));
            dispatch(setToInitialState());
        };
    }, []);

    useEffect(() => {
        if (previewPosition) {
            previewPositionRef.current = previewPosition;
        }
    }, [previewPosition]);

    const disposeCabinets = () => {
        for (const cabinet of cabinetGroupListRef.current) {
            deleteCabinetMesh(cabinet, false, cabinetGroupListRef);
        }

        cabinetGroupListRef.current = [];
    };

    const renderProducts = useDebouncedCallback(async () => {
        if (!roomPlannerData) return;
        const currentRenderGeneration = ++productRenderGeneration.current;
        const isLatestProductRender = () =>
            currentRenderGeneration === productRenderGeneration.current;

        isInitialRender.current = false;
        publishRenderedProductCount(0);

        const dimensions = getRoomDimensionsComputed();
        let currentWidth = Number(dimensions.computedWidth);
        const wallWidth = Number(dimensions.roomWidth / 2);
        let row = Number(1);

        disposeCabinets();

        // Warm the texture cache for every product before the render loop
        // starts so all texture network requests fire in parallel. The
        // sequential loop below then finds textures already cached or
        // in-flight, eliminating the per-cabinet texture download stall.
        for (const product of roomPlannerData) {
            preloadCabinetTextures(product as unknown as PreviewFormValues);
        }

        let paintedProductCount = 0;
        for (const [productIndex, product] of roomPlannerData.entries()) {
            if (!isLatestProductRender()) return;

            try {
                if (currentWidth + 120 > wallWidth) {
                    currentWidth = Number(dimensions.computedWidth);
                    row += 1;
                }
                const productWithTemplate =
                    product as RoomPlannerProductWithTemplate;

                const {widthIncrement} = renderCabinetGroup(
                    productWithTemplate,
                    {
                        ...dimensions,
                        positionX: (currentWidth || 0) + 5,
                    },
                    String(room.id),
                    cabinetGroupListRef,
                    row
                );
                currentWidth += widthIncrement;
                paintedProductCount += 1;
                publishRenderedProductCount(paintedProductCount);
            } catch {
                // alert(err);
            }

            const hasMoreProducts = productIndex < roomPlannerData.length - 1;
            if (hasMoreProducts) {
                await waitForNextPaint();
            }
        }

        if (!isLatestProductRender()) return;

        // Update currentWall for all cabinets after initial render.
        // Placeholders load from the room layout response, not from this pass.
        setTimeout(() => {
            if (!isLatestProductRender()) return;

            const cabinetGroupList = cabinetGroupListRef.current;
            if (cabinetGroupList.length > 0) {
                productEvents.updateAllCabinetsCurrentWall(CameraSetup.scene);
                productEvents.clearEvents();
                productEvents.attachEvents({
                    scene: CameraSetup.scene,
                    camera: CameraSetup.camera,
                    controls: CameraSetup.controls,
                    roomType,
                });
                if (!isGeneratingReport) {
                    productEvents.revalidateOverlaps();
                }
            }
            setProductsReady(true);
        }, 100);
    }, 200);

    // Live product-form rebuild (edit sidebar / cancel / save). Mesh work lives
    // in helpers/cabinetUpdate; this callback only sequences those steps.
    const updateCabinet = useDebouncedCallback(() => {
        if (
            !isCabinetUpdateReady(
                productValuesForUpdate,
                selectedProductValue,
                cabinetGroupListRef?.current
            )
        ) {
            return;
        }

        const cabinetsRef = cabinetGroupListRef;
        const isSwitchingCabinet = productValuesForUpdate.isSwitchingCabinet;
        // Match the form payload to the scene mesh (and undo / QFP side-effects).
        const cabinets = findCabinetsForProductUpdate(
            cabinetsRef.current,
            selectedProductValue,
            productValuesForUpdate
        );
        const productValue = resolveProductValueForUpdate(
            selectedProductValue,
            productValuesForUpdate,
            cabinets
        );

        const editSessionRefs = {
            originalDepthRef,
            originalHeightRef,
            originalWidthRef,
            originalExteriorThicknessRef,
            attachedSideOriginalPositionsRef,
        };

        cabinets.forEach((cabinet: CabinetMesh) => {
            scheduleCabinetDeselect(cabinet);

            // First form tick after opening the editor: snapshot originals, skip rebuild.
            if (
                tryBeginCabinetUpdateSession({
                    updateInitialized,
                    isSwitchingCabinet,
                    cabinet,
                    allCabinets: cabinetsRef.current ?? [],
                    attachedSideOriginalPositionsRef,
                })
            ) {
                return;
            }

            if (!cabinet || !productValue) return;

            // Delete + re-render, then nudge position / attachments / wall clamp.
            const result = applyCabinetFormUpdate({
                cabinet,
                productValue,
                productValuesForUpdate,
                isSwitchingCabinet,
                cabinetGroupListRef: cabinetsRef,
                refs: editSessionRefs,
                roomId: String(room.id),
                roomType,
                camera: CameraSetup.camera,
            });
            if (!result) return;

            const {updatedCabinetGroup, attachedSideProducts} = result;

            // Re-select the rebuilt mesh and revalidate overlaps.
            scheduleCabinetUpdateUi({
                updatedCabinetGroup,
                previousCabinet: cabinet,
                cabinets,
                isSwitchingCabinet,
                productValue,
                renderer: renderer?.current,
                camera: CameraSetup.camera,
                dispatch,
                setSelectedCabinetGroup,
                checkIfOverlapsOtherCabinets,
            });
            // Clear session originals and auto-save only when the user hits Save.
            persistCabinetUpdateOnSave(
                productValuesForUpdate,
                editSessionRefs,
                updatedCabinetGroup,
                attachedSideProducts
            );
            syncCabinetDoorAfterUpdate(updatedCabinetGroup);

            dispatch(setProductValuesForUpdate(null));
        });
    }, 200);

    useEffect(() => {
        if (
            !isInitialRender.current &&
            roomPlannerData &&
            roomPlannerData.length > 0 &&
            !isSwitchingRooms.current
        ) {
            const {added, removed} = getProductDifferences(
                roomPlannerData,
                cabinetGroupListRef.current
            );
            if (removed && removed.length > 0) {
                removed.forEach((cabinet) => {
                    deleteCabinetMesh(cabinet, false, cabinetGroupListRef);
                });
            }

            if (added && added.length > 0) {
                const dimensions = getRoomDimensionsComputed();
                let currentWidth = Number(dimensions.computedWidth);
                const wallWidth = Number(dimensions.roomWidth / 2);
                let row = Number(1);

                for (const product of added) {
                    try {
                        if (currentWidth + 120 > wallWidth) {
                            currentWidth = Number(dimensions.computedWidth);
                            row += 1;
                        }
                        const productWithTemplate =
                            product as RoomPlannerProductWithTemplate;
                        const {widthIncrement} = renderCabinetGroup(
                            productWithTemplate,
                            {
                                ...dimensions,
                                positionX: currentWidth + 5,
                            },
                            String(room.id),
                            cabinetGroupListRef,
                            row
                        );
                        currentWidth += widthIncrement;
                    } catch (err) {
                        // alert(err);
                    }
                }
            }
        }
    }, [roomPlannerData]);

    useEffect(() => {
        void updateCabinet();
    }, [productValuesForUpdate, selectedProductValue, roomPlannerData]);

    useEffect(() => {
        if (!isEditing || !cabinetId || !qfpSelectedProduct) {
            return;
        }

        const cabinet = cabinetGroupListRef.current?.find(
            (group) =>
                String(group.userData.values?.job_cabinet_id) ===
                    String(cabinetId) ||
                String(group.userData.values?.id) === String(cabinetId)
        );
        if (!cabinet?.userData.isLShapedProduct) return;

        const cabinetValues =
            (cabinet.userData.values as Partial<typeof qfpSelectedProduct>) ??
            {};

        const changed =
            Number(qfpSelectedProduct.cabinet_left_width) !==
                Number(cabinetValues.cabinet_left_width) ||
            Number(qfpSelectedProduct.cabinet_right_width) !==
                Number(cabinetValues.cabinet_right_width) ||
            Number(qfpSelectedProduct.cabinet_left_depth) !==
                Number(cabinetValues.cabinet_left_depth) ||
            Number(qfpSelectedProduct.cabinet_right_depth) !==
                Number(cabinetValues.cabinet_right_depth) ||
            Number(qfpSelectedProduct.cabinet_height) !==
                Number(cabinetValues.cabinet_height);
        if (!changed) return;

        dispatch(
            setProductValuesForUpdate({
                ...qfpSelectedProduct,
                job_cabinet_id:
                    cabinetValues.job_cabinet_id ??
                    qfpSelectedProduct.job_cabinet_id,
                sideEffectFromUpdate: true,
            } as unknown as Parameters<typeof setProductValuesForUpdate>[0])
        );
    }, [
        qfpSelectedProduct,
        isEditing,
        cabinetId,
        cabinetGroupListRef,
        dispatch,
    ]);

    useEffect(() => {
        if (cabinetGroupListRef.current) {
            cabinetGroupListRef.current.forEach((cabinet) => {
                cabinet.visible = showCabinets;
            });
        }
    }, [showCabinets]);

    useEffect(() => {
        if (cabinetId && productId && cabinetGroupListRef.current) {
            const cabinet = cabinetGroupListRef.current.find(
                (group) => group.userData.values.id == cabinetId
            );

            if (!cabinet) return;

            dispatch(setSelectedProductValue(cabinet.userData.values));
        }
    }, [cabinetId, productId]);

    const previousDoorFrontsMode = useRef(doorFrontsMode);
    const doorFrontsRequest = useRef(0);
    useEffect(() => {
        if (previousDoorFrontsMode.current === doorFrontsMode) return;
        previousDoorFrontsMode.current = doorFrontsMode;

        const request = ++doorFrontsRequest.current;
        const animate = doorFrontsMode !== 'hide';
        const cabinets = cabinetGroupListRef.current ?? [];
        const poseDoor: DoorPoseTrigger = (group, eventName, params) =>
            productEvents.triggerEventsAsync(group, eventName, params);

        void Promise.all(
            cabinets.map((cabinet) =>
                applyDoorFrontsMode(cabinet, doorFrontsMode, poseDoor, {
                    animate,
                })
            )
        ).finally(() => {
            if (doorFrontsRequest.current !== request || !animate) return;
            dispatch(setDoorFrontsAnimating(false));
        });
    }, [doorFrontsMode, cabinetGroupListRef, dispatch]);

    useEffect(() => {
        if (cabinetGroupListRef.current) {
            cabinetGroupListRef.current.forEach((product) => {
                productEvents.triggerEvents(
                    product,
                    'onToggleWireframe',
                    isWireframeMode
                );
            });
        }

        if (wallRefs.current) {
            wallRefs.current.forEach((wall) => {
                productEvents.triggerEvents(
                    wall as unknown as CabinetMesh,
                    'onToggleWireframe',
                    isWireframeMode
                );
            });
        }
    }, [isWireframeMode]);

    useEffect(() => {
        if (cabinetId) {
            updateInitialized.current = false;
        }
    }, [cabinetId]);

    // Setup keyboard controls
    useEffect(() => {
        // Wait until scene, camera, and container are initialized
        if (
            !CameraSetup.scene ||
            !CameraSetup.camera ||
            !mountRef.current ||
            !roomType ||
            (cabinetGroupListRef.current.length === 0 &&
                (placeholderListRef?.current.length ?? 0) === 0)
        ) {
            return;
        }

        const keyboardHandler = createKeyboardHandler({
            selectedCabinetGroup,
            debouncedSave,
            setSelectedCabinetGroup,
            products: cabinetGroupListRef.current.concat(
                placeholderListRef?.current ?? []
            ),
            scene: CameraSetup.scene,
            camera: CameraSetup.camera,
            roomType,
            containerRef: mountRef,
            hiddenWall,
            isRoomPreview,
        });

        // Attach keyboard event listener with capture to prevent browser default behavior
        document.addEventListener('keydown', keyboardHandler, true);

        return () => {
            // Cleanup keyboard event listener and any in-flight held movement
            stopHeldMovement();
            clearKeyboardSnapGuide();
            document.removeEventListener('keydown', keyboardHandler, true);
        };
    }, [
        selectedCabinetGroup,
        roomType,
        setSelectedCabinetGroup,
        debouncedSave,
        cabinetGroupListRef.current.length,
        placeholderListRef?.current.length,
        setDeleteHandler,
        hiddenWall,
    ]);

    const checkForBackupProductValues = useDebouncedCallback(
        (trigger?: boolean, backupParam?: PreviewFormValues) => {
            if ((!isEditing || trigger) && backupProductValues) {
                const backup = {
                    ...(backupParam || backupProductValues),
                    isSwitchingCabinet: trigger,
                    fromBackup: true,
                } as unknown as PreviewFormValues;
                dispatch(setProductValuesForUpdate(backup));
                dispatch(setBackupProductValues(null));
                originalDepthRef.current = null;
                originalHeightRef.current = null;
                originalWidthRef.current = null;
                originalExteriorThicknessRef.current = null;
            }
        },
        200
    );

    useEffect(() => {
        checkForBackupProductValues();
    }, [isEditing, dispatch]);

    useEffect(() => {
        if (layouts && layouts.success === 1 && layouts.layout) {
            const layout = layouts.layout;
            const isRectangular = layout.layout_room_shape_id === 1;

            dispatch(setRoomLayoutModalOpen(false));
            dispatch(setLayoutId(layout.id));
            dispatch(setLayoutData(layout));
            dispatch(setIsTopView(false));

            if (roomType == null) {
                dispatch(
                    setRoomType(isRectangular ? 'RECTANGULAR' : 'LSHAPED')
                );
                if (isRectangular) {
                    dispatch(
                        setRectangularDimension({
                            width: layout.room_dimensions.width,
                            length: layout.room_dimensions.length,
                            height: layout.room_dimensions.height,
                        })
                    );
                } else {
                    dispatch(
                        setLShapedDimension({
                            leftWidth: layout.room_dimensions.leftWidth,
                            rightWidth: layout.room_dimensions.rightWidth,
                            leftDepth: layout.room_dimensions.leftDepth,
                            rightDepth: layout.room_dimensions.rightDepth,
                            height: layout.room_dimensions.height,
                        })
                    );
                }
            }
        }
    }, [roomId, dispatch, layouts, roomType]);

    // Set room index for preview mode
    useEffect(() => {
        if (
            isRoomPreview &&
            roomPreviewData?.product_info?.RoomNumber != null
        ) {
            // Convert 1-based RoomNumber to 0-based index
            const roomIndex =
                Number(roomPreviewData.product_info.RoomNumber) - 1;
            dispatch(setRoomPlannerRoomIndex(roomIndex));
        }
    }, [isRoomPreview, roomPreviewData, dispatch]);

    // undefined while this room's layout is still loading. The shared empty
    // list means the room was returned and has no appliances.
    const placeholdersForRoom = isRoomPreview
        ? roomPreviewData?.layout
            ? (roomPreviewData.layout.layout_room_placeholders ??
              EMPTY_ROOM_PLACEHOLDERS)
            : undefined
        : isLayoutUninitialized || !layoutsForCurrentRoom?.layout
          ? undefined
          : (layoutsForCurrentRoom.layout.layout_room_placeholders ??
            EMPTY_ROOM_PLACEHOLDERS);

    // Placeholders follow the room layout request, the same way cabinets follow
    // the room product request. An empty list for this room draws nothing.
    useEffect(() => {
        const generation = ++placeholderRoomLoad.current;
        const isCurrent = () => generation === placeholderRoomLoad.current;

        if (!placeholderListRef || !cabinetGroupListRef) return;
        clearPlaceholdersFromScene(placeholderListRef, cabinetGroupListRef);
        if (
            placeholdersForRoom === undefined ||
            !CameraSetup.scene ||
            roomSceneGeneration === 0
        ) {
            return;
        }

        void restorePlaceholdersForRoom(
            CameraSetup.scene,
            placeholderListRef,
            cabinetGroupListRef,
            placeholdersForRoom,
            isCurrent
        );
    }, [
        roomId,
        roomSceneGeneration,
        placeholdersForRoom,
        placeholderListRef,
        cabinetGroupListRef,
    ]);

    // Hard refresh the room planner page when changing rooms.
    // It helps clearing the memory of the previous room.
    // Can be improved if we have a better way to reload the room planner.
    useEffect(() => {
        if (!isInitialRender.current) {
            isSwitchingRooms.current = true;
            window.location.reload();
        }
        return () => {
            clearEvents();
        };
    }, [roomId]);

    useEffect(() => {
        layoutIdRef.current = layoutId;
    }, [layoutId]);

    useEffect(() => {
        isEditingRoomDimensionRef.current = isEditingRoomDimension;
    }, [isEditingRoomDimension]);

    useEffect(() => {
        const getWallHeight = (): number => {
            if (roomType === 'RECTANGULAR') {
                return rectangularDimension.height;
            }
            if (roomType === 'LSHAPED') {
                return lShapedDimension.height;
            }
            return 0;
        };

        const wallHeight = getWallHeight() * 0.2;

        if (!cabinetGroupListRef.current || wallHeight <= 0) {
            return;
        }

        cabinetGroupListRef.current.forEach((cabinet) => {
            if (cabinet.userData.wallHeight !== wallHeight) {
                cabinet.userData.adaptToWallHeightChange(
                    wallHeight,
                    !isEditingRoomDimensionRef.current
                );
            }
        });
    }, [rectangularDimension?.height, lShapedDimension?.height, roomType]);

    useEffect(() => {
        // auto switch filler fixing side
        handleFillerSwitching(cabinetGroupListRef.current, wallRefs.current);
    }, [cabinetGroupListRef.current, wallRefs.current]);

    return {
        roomPlannerData,
        isGeneratingReport,
        mountRef,
        camera: CameraSetup.camera,
        scene: CameraSetup.scene,
        renderer,
        previewPositionRef,
        isInitialRender,
        dialog,
        sideButtonGroupsRef,
        productsReady,
        isRoomSceneReady: roomSceneGeneration > 0,
        // Only block the view on startup when an existing room actually has
        // products to draw. New rooms and empty rooms render straight away.
        showStartupOverlay:
            !productsReady && hasSavedRoomLayout && hasProductsToLoad,
        subscribeToProductRenderProgress,
        getRenderedProductCount,
        totalProductCount: roomPlannerData?.length ?? 0,
    };
};

export default useRoomPlanner;
